import { useEffect, useState } from 'react'
import { IconTruckDelivery } from '@tabler/icons-react'
import clsx from 'clsx'
import Link from 'next/link'
import { useSession } from 'next-auth/react'

import Button from 'shared/ui/Button'
import { Modal, ModalDescription, ModalTitle } from 'shared/ui/Modal'
import { Tooltip } from 'shared/ui/Tooltip'
import { KEYBOARD_KEYS } from 'shared/ui/types'
import { noop } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'

import useCartStore from '../../../store/cartStore'
import { useFirstAvailableDeliverySlots } from '../hooks/useFirstAvailableDeliverySlots'

import styles from './DeliverySlotTimer.module.scss'

interface DeliverySlotTimerProps {
  closeMobileModal?: () => void
}

const DeliverySlotTimer = ({ closeMobileModal }: DeliverySlotTimerProps) => {
  const [timeLeft, setTimeLeft] = useState<number>(0)
  const { cart, extendSlot, refreshCart } = useCartStore()
  const { firstAvailableDeliverySlot } = useFirstAvailableDeliverySlots()
  const [modalOpen, setModalOpen] = useState(false)
  const [wasClosed, setWasClosed] = useState(false)
  const session = useSession()

  const deliverySlot = cart?.deliverySlot || null

  const handleOpen = () => setModalOpen(true)
  const handleClose = () => {
    setModalOpen(false)
    setWasClosed(true)
    // @sieradz - useState from DeliveryDropdown - NavigationMobile
    if (typeof closeMobileModal === 'function') {
      closeMobileModal()
    }
  }

  useEffect(() => {
    if (cart && cart.deliverySlotReservedAt && timeLeft > 0 && !wasClosed) {
      if (timeLeft >= 10.0 && timeLeft <= 60.0 && !modalOpen) {
        handleOpen()
      }

      if (timeLeft <= 1.0 && modalOpen) {
        handleClose()
      }
    }

    if (cart && cart.deliverySlotReservedAt && timeLeft === 0) {
      setTimeout(() => {
        setModalOpen(false)
        refreshCart().then(noop).catch(noop)
      }, 2000)
    }
  }, [timeLeft])

  useEffect(() => {
    if (cart && cart.deliverySlotReservedAt) {
      const deliverySlotReservedAt = new Date(cart.deliverySlotReservedAt)
      const targetDate = new Date(deliverySlotReservedAt)

      targetDate.setMinutes(targetDate.getMinutes() + 20)

      const calculateTimeLeft = () => {
        const now = new Date()
        const remainingTime = Math.max(
          0,
          (targetDate.getTime() - now.getTime()) / 1000
        )

        setTimeLeft(remainingTime)
      }

      calculateTimeLeft()
      const interval = setInterval(calculateTimeLeft, 1000)

      return () => clearInterval(interval)
    }

    return
  }, [cart])

  const formatTime = timeInSeconds => {
    const minutes = Math.floor((timeInSeconds % 3600) / 60)
    const seconds = Math.floor(timeInSeconds % 60)

    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }

  const handleSubmit = async () => {
    try {
      await extendSlot(session)
      setModalOpen(false)
      setWasClosed(false)
    } catch (err) {
      console.error(err)
    }
  }

  return cart ? (
    <>
      <Link
        className={clsx(styles.timerWrapper, {
          [styles.timerWrapperWithTimer]: timeLeft > 0
        })}
        href={PATHS.delivery}
        onClick={closeMobileModal}
        tabIndex={0}
      >
        <IconTruckDelivery />
        <div>
          {isNotNilOrEmpty(deliverySlot) ? (
            <>
              <div className={styles.timerLabel}>Rezerwacja terminu dostawy</div>
              <div className={styles.timerDetails}>
                <div className={styles.buttonLabel}>
                  <div className={styles.dateLabel}>{deliverySlot?.date || '---'}</div>
                  <div>
                    {deliverySlot?.start} - {deliverySlot?.end}
                  </div>
                </div>
                {timeLeft > 0 && (
                  <Tooltip
                    id='delivery-slot-timer-tooltip'
                    title='Pozostały czas rezerwacji terminu.'
                  >
                    <div
                      className={styles.timerValue}
                      tabIndex={0}
                      onKeyDown={e => {
                        if (
                          e.key === KEYBOARD_KEYS.ENTER ||
                          e.key === KEYBOARD_KEYS.SPACE
                        ) {
                          e.preventDefault()
                          handleOpen()
                        }
                      }}
                      onClick={e => {
                        e.preventDefault()
                        handleOpen()
                      }}
                    >
                      <div>{formatTime(timeLeft)}</div>
                    </div>
                  </Tooltip>
                )}
              </div>
            </>
          ) : isNotNilOrEmpty(firstAvailableDeliverySlot) ? (
            <>
              <div className={styles.timerLabel}>Najbliższa możliwa dostawa</div>
              <div className={styles.buttonLabel}>
                {firstAvailableDeliverySlot?.date}{' '}
                {firstAvailableDeliverySlot?.start} -{' '}
                {firstAvailableDeliverySlot?.end}
              </div>
            </>
          ) : (
            <>
              <div className={styles.timerLabel}>Dostawa</div>
              <div className={styles.buttonLabel}>Wybierz</div>
            </>
          )}
        </div>
      </Link>
      <Modal
        id='delivery-slot-timer-modal'
        open={modalOpen}
        onClose={handleClose}
      >
        <ModalTitle>Potwierdź czas dostawy</ModalTitle>
        <ModalDescription>
          <div>Twoja rezerwacja terminu dostawy straci ważność za:</div>
          <div className={styles.modalTimer}>{formatTime(timeLeft)}</div>
          <div>Czy chcesz wydłużyć czas rezerwacji?</div>
        </ModalDescription>
        <div className={styles.buttonsWrapper}>
          <Button
            fullWidth
            type='button'
            variant='primary'
            onClick={handleSubmit}
          >
            Potwierdź
          </Button>
          <Button fullWidth variant='neutralOutline' onClick={handleClose}>
            Anuluj
          </Button>
        </div>
      </Modal>
    </>
  ) : null
}

export default DeliverySlotTimer
